'use client'

import { useTranslations } from 'next-intl'
import { useOptimizedCartStore } from '@/stores/optimized-cart-store'
import { toast } from 'sonner'
import { useSWRConfig } from 'swr'
import { Product } from '@/app/[locale]/admin/products/types'
import { Button } from '@/components/ui/button'
import { useState } from 'react'
import { useRouter } from 'next/navigation'
import dynamic from 'next/dynamic'

// Dynamically import client components with lazy loading
const ShellButton = dynamic(() => import('@/components/ShellButton'), {
  loading: () => <div className="h-12 w-full bg-surface animate-pulse rounded-lg"></div>,
  ssr: false
});

interface AddToCartButtonProps {
  product: Product
  selectedColor: string
  quantity: number
  isKgPrice: boolean
  disabled?: boolean
}

// Legacy props interface (used by AddToCartButtonWrapper)
interface LegacyProps {
  productId: string;
  moqGrams: number;
  addToCartText: string;
  quantityLabel: string;
  defaultUnit?: string;
  unitOptions?: Array<{ value: string; label: string }>;
  defaultQuantity?: number;
  conversionFactor?: number;
  productOptions?: Array<{ name: string; extraPrice: number; skuId?: string }>;
  kg_price?: boolean;
}

// Type guard to distinguish between legacy and new props
function isLegacyProps(props: AddToCartButtonProps | LegacyProps): props is LegacyProps {
  return typeof (props as LegacyProps).productId === 'string';
}

// ----------------------------------------------------------------------
// Universal AddToCartButton that supports BOTH prop shapes
// ----------------------------------------------------------------------
function AddToCartButtonUniversal(props: AddToCartButtonProps | LegacyProps) {
  const t = useTranslations('product')
  const addItemToCart = useOptimizedCartStore(state => state.addItem)

  // ---------------- Legacy rendering ----------------
  if (isLegacyProps(props)) {
    return <LegacyAddToCartButton {...props as LegacyProps} />;
  }

  // ---------------- New rendering (Product prop) ----------------
  const { product, selectedColor, quantity, disabled } = props as AddToCartButtonProps

  const selectedOption = product.options?.find(opt => opt.name === selectedColor)
  const skuId = selectedOption?.skuId

  const handleAddToCartNew = () => {
    addItemToCart(product.id, quantity, selectedColor, skuId)
    useOptimizedCartStore.getState().openSidebar(); // Open the cart sidebar after adding item
    toast.success(t('addedToCart'))
  }

  return (
    <Button disabled={disabled} onClick={handleAddToCartNew} className="w-full">
      {t('addToCart')}
    </Button>
  )
}

function LegacyAddToCartButton(props: LegacyProps) {
  const { mutate } = useSWRConfig();
  const router = useRouter();
  const {
    productId,
    moqGrams,
    addToCartText,
    quantityLabel,
    defaultUnit = 'kg',
    unitOptions = [
      { value: 'kg', label: 'kg' },
      { value: 'g', label: 'g' }
    ],
    defaultQuantity = 1,
    conversionFactor = 1000,
    productOptions,
    kg_price = true
  } = props;

  const [quantity, setQuantity] = useState<number>(defaultQuantity);
  const [unit, setUnit] = useState<string>(defaultUnit);
  const [selectedOption, setSelectedOption] = useState<string | undefined>(
    productOptions && productOptions.length > 0 ? productOptions[0].name : undefined
  );
  const [isAdding, setIsAdding] = useState(false);

  const getGrams = () => kg_price ? (unit === 'kg' ? quantity * conversionFactor : quantity) : quantity;
  const isValidQty = () => kg_price ? getGrams() >= moqGrams : quantity >= 1;
  const skuId = productOptions?.find(o => o.name === selectedOption)?.skuId;

  const handleAdd = async () => {
    if (!isValidQty()) return alert('Invalid quantity');
    setIsAdding(true);
    try {
      useOptimizedCartStore.getState().addItem(productId, getGrams(), selectedOption, skuId);
      useOptimizedCartStore.getState().openSidebar(); // Open the cart sidebar after adding item
      toast.success(addToCartText);
      mutate('/api/cart/count');
      router.refresh();
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center">
        <label htmlFor="quantity-input" className="text-sm font-bold text-text-main mr-3">
          {quantityLabel}:
        </label>
        <input
          id="quantity-input"
          type="number"
          min={kg_price ? (unit === "kg" ? moqGrams / conversionFactor : moqGrams) : 1}
          value={quantity}
          onChange={(e) => setQuantity(Math.max(0, Number(e.target.value)))}
          className="w-20 px-3 py-2 bg-surface text-text-main focus:outline-none focus:ring-1 focus:ring-primary rounded-l-lg"
          aria-label={`${quantityLabel} ${kg_price ? `in ${unit}` : 'in units'}`}
        />
        {kg_price && (
          <div className="relative">
            <label htmlFor="unit-select" className="sr-only">Unit</label>
            <select
              id="unit-select"
              value={unit}
              onChange={(e) => {
                const newUnit = e.target.value;
                setUnit(newUnit);
                
                // Adjust quantity when switching units to maintain equivalent value
                if (newUnit !== unit) {
                  if (newUnit === "g" && unit === "kg") {
                    // Convert kg to g (multiply by 1000)
                    setQuantity(quantity * conversionFactor);
                  } else if (newUnit === "kg" && unit === "g") {
                    // Convert g to kg (divide by 1000)
                    setQuantity(Math.max(1, Math.floor(quantity / conversionFactor)));
                  }
                }
              }}
              className="h-full px-3 py-2 rounded-r-lg bg-surface-hover text-text-main appearance-none pr-8 focus:outline-none focus:ring-1 focus:ring-primary"
              aria-label="Select unit"
            >
              {unitOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <svg className="w-4 h-4 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
          </div>
        )}
      </div>
      
      {/* Product Options Selector */}
      {productOptions && productOptions.length > 0 && (
        <div className="mb-4">
          <label htmlFor="option-select" className="block text-sm font-bold text-text-secondary mb-1">
            Color/Option:
          </label>
          <div className="relative">
            <select
              id="option-select"
              value={selectedOption}
              onChange={(e) => setSelectedOption(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-bg-secondary text-text-main appearance-none pr-8 focus:outline-none focus:ring-1 focus:ring-primary"
              aria-label="Select product option"
            >
              {productOptions.map(option => (
                <option key={option.name} value={option.name}>
                  {option.name}{option.extraPrice > 0 ? ` (+€${option.extraPrice})` : ''}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <svg className="w-4 h-4 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
          </div>
        </div>
      )}

      <div className="pt-4 w-full" onClick={(e) => e.stopPropagation()}>
        <div className="pointer-events-none">
          <ShellButton
            type="button"
            variant="white"
            onClick={handleAdd}
            disabled={isAdding || !isValidQty()}
            className="w-full pointer-events-auto"
          >
            {isAdding ? '...' : addToCartText}
          </ShellButton>
        </div>
      </div>
    </div>
  );
}

// Export default for dynamic()
export default AddToCartButtonUniversal; 